<template>
  <div
    id="app"
    :class="[$options.$styles.wrapper]"
    spellcheck="false"
    v-calc-check
    v-ios-field-defocuser
    v-contrast-toggler="contrastIsHigh"
  >
    <vue-headful title="BattleFX | Roleplaying battle effects tracker" />
    <svg-defs />

    <the-header />
    <fader>
      <div
        :class="{
          [`${$options.$styles.contentWrapper}`]: true,
          [`${$options.$styles.toolbarTop__contentWrapper}`]: toolbarIsTop,
        }"
      >
        <main class="m-auto w-full py-8"><router-view /></main>
        <footer
          class="text-xs max-sm:mb-1 absolute text-center text-accent-20 relative z-2"
        >
          <p class="absolute w-full pointer-events-none -mt-3">
            <router-link
              data-test="logout"
              to="logout"
              v-if="isTesting"
              class="pointer-events-auto"
            >
              log out
            </router-link>
            <span v-if="isTesting">|</span>
            &copy; Joel Birch
            {{ getCurrentYear }}
          </p>
        </footer>
      </div>
    </fader>
  </div>
</template>

<script>
const $styles = {
  wrapper: ` bg-bg flex flex-1 flex-col text-readable overflow-hidden `,
  contentWrapper: ` flex flex-1 flex-col justify-center mx-auto pb-16 pseudo-gradient-to-top px-11 relative u-wrapper w-full `,
  toolbarTop__contentWrapper: ` sm:pb-8 sm:pt-12 `,
}

import '@/styles/_base.variables.css'
import '@/styles/_base.global.css'
import '@/styles/_base.animations.css'
import '@/styles/_objects.obvious-button.css'
import '@/styles/_objects.button.css'

import firebase from '@/utilities/firebase-config'

import SvgDefs from '@/components/SvgDefs'
import TheHeader from '@/components/TheHeader'

import CalcCheck from '@/directives/CalcCheck'
import ContrastToggler from '@/directives/ContrastToggler'
import IosFieldDefocuser from '@/directives/IosFieldDefocuser'

import { mapActions, mapGetters, mapState } from 'vuex'

import { guestUser } from './store/accounts'
import { getCurrentYear, isTesting } from '@/utilities/helpers'

export default {
  name: `App`,
  components: {
    SvgDefs,
    TheHeader,
  },

  directives: {
    CalcCheck,
    ContrastToggler,
    IosFieldDefocuser,
  },

  data: () => ({
    ui: {
      contrastIsHigh: false,
      toolbarIsTop: true,
    },
  }),

  provide() {
    return {
      ui: this.ui,
    }
  },

  computed: {
    ...mapGetters(`accounts`, [`loggedIn`, `user`]),
    ...mapGetters(`settings`, [`toolbarIsTop`, `contrastIsHigh`]),
    ...mapState(`settings`, [`toolbar`]),
    getCurrentYear,
    isTesting,
  },

  methods: {
    ...mapActions(`battles`, [`bindUserBattlesRef`]),
    ...mapActions(`favourites`, [`bindFavouritesRef`]),
    ...mapActions(`accounts`, [`setUser`]),
  },

  updated() {
    this.ui.toolbarIsTop = this.toolbarIsTop
  },

  created() {
    this.bindUserBattlesRef(this.user.uid)
    firebase.auth().onAuthStateChanged(changedUser => {
      changedUser = changedUser || guestUser
      this.setUser(changedUser)
      this.bindUserBattlesRef(changedUser.uid)
      this.bindFavouritesRef(changedUser.uid)
    })
  },

  $styles,
}
</script>

<style lang="postcss">
[v-cloak] {
  display: none !important;
}

.contents {
  display: contents;
}

@tailwind utilities;

.u-beta {
  @apply font-sans opacity-75 text-white text-var-xs uppercase tracking-wide;
}

[id='app'] {
  fill: currentColor; /* Safari won't pass this on from body to svgs, so set it here */

  &.js-no-advanced-calc {
    background-color: hsl(209, 86%, 13%);
  }
}
</style>

<style lang="postcss" scoped>
@screen max-sm {
  .pseudo-gradient-to-top {
    &:after {
      @apply fixed l r b pt-20 z-1 pointer-events-none;
      content: '';
      background: linear-gradient(
          to top,
          var(--bg-color) 50%,
          var(--bg-full-trans-color)
        ),
        linear-gradient(to top, var(--bg-color) 50%, var(--bg-full-trans-color));
    }
  }
}
</style>
